import {
  Box,
  Card,
  Flex,
  Heading,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { makeRequest } from "@/network/api";

type DailySpinCategoryStats = {
  count_per_category: number;
  reward_category: string;
  proportion_percent: string;
};

type DailySpinDailyStats = {
  day: string;
  total_spins: number;
};

type DailySpinJackpotStats = {
  day: string;
  dow_thu_zero: number;
  total_spins: number;
};

type PortalStatsData = {
  dailySpinCategoryStats: DailySpinCategoryStats[];
  dailySpinDailyStats: DailySpinDailyStats[];
  dailySpinJackpotStats: DailySpinJackpotStats[];
};

export const PortalStats = () => {
  const [stats, setStats] = useState<PortalStatsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPortalStats = async () => {
    try {
      setIsLoading(true);
      const response = await makeRequest("/admin/portal/stats");
      const responseData = await response.json();
      setStats(responseData.data.dailySpinStats);
    } catch (error) {
      console.error("Error fetching portal stats:", error);
      setError("Failed to load portal stats");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data loading is synchronized with mounting this admin view.
  /* oxlint-disable react/set-state-in-effect */
  useEffect(() => {
    fetchPortalStats();
  }, []);
  /* oxlint-enable react/set-state-in-effect */

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="50vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} maxW="1200px" mx="auto">
        <Heading mb={6}>Portal Stats</Heading>
        <Card.Root p={4}>
          <Card.Body>
            <Text color="red.500">{error}</Text>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box p={4} maxW="1200px" mx="auto">
        <Heading mb={6}>Portal Stats</Heading>
        <Card.Root p={4}>
          <Card.Body>
            <Text color="gray.500">No stats available</Text>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  }

  return (
    <Box p={0} maxW="1200px" mx="auto">
      <Heading mb={6}>Portal Stats</Heading>

      {/* Daily Spin Category Breakdown */}
      <Card.Root p={4} mb={6}>
        <Card.Header>
          <Heading size="md">Daily Spin Category Breakdown</Heading>
        </Card.Header>
        <Card.Body>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Reward Category</Table.ColumnHeader>
                <Table.ColumnHeader>Count</Table.ColumnHeader>
                <Table.ColumnHeader>Proportion</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {stats.dailySpinCategoryStats.map((category, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{category.reward_category}</Table.Cell>
                  <Table.Cell>
                    {category.count_per_category.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell>{category.proportion_percent}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>

      {/* Daily Spin Counts by Day */}
      <Card.Root p={4} mb={6}>
        <Card.Header>
          <Heading size="md">Daily Spin Counts by Day</Heading>
        </Card.Header>
        <Card.Body>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Day</Table.ColumnHeader>
                <Table.ColumnHeader>Total Spins</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {stats.dailySpinDailyStats.map((day, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{day.day}</Table.Cell>
                  <Table.Cell>{day.total_spins.toLocaleString()}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>

      {/* Jackpot Spins by Day */}
      <Card.Root p={4}>
        <Card.Header>
          <Heading size="md">Jackpot Spins by Day</Heading>
        </Card.Header>
        <Card.Body>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Day</Table.ColumnHeader>
                <Table.ColumnHeader>Day of Week (Thu=0)</Table.ColumnHeader>
                <Table.ColumnHeader>Jackpot Spins</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {stats.dailySpinJackpotStats.map((day, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{day.day}</Table.Cell>
                  <Table.Cell>{day.dow_thu_zero}</Table.Cell>
                  <Table.Cell>{day.total_spins.toLocaleString()}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>
    </Box>
  );
};
